import React, { useEffect, useState } from 'react';
import { callApi } from '../../api/call';
import { buildDeluxeSavePayload } from '../credentials/buildDeluxeSavePayload';
import '../../authorize/components/AuthorizeConnectCard.css';

const HELP_TEXT =
  'Enter Deluxe Hosted Payment Form credentials to accept card payments for online bookings, deposits, and pre-payments.';

const EMPTY_FORM = {
  clientId: '',
  clientSecret: '',
  accessToken: '',
  signatureKey: '',
  environment: 'SANDBOX',
};

const DeluxeConnectCard = ({ companyId }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({
    connected: false,
    environment: 'SANDBOX',
  });
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('idle');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (!companyId) return undefined;
    callApi(`/api/companies/${encodeURIComponent(companyId)}/payments/deluxe/get`, { companyId })
      .then((res) => {
        setStatus(res.data);
        setForm((prev) => ({
          ...prev,
          environment: res.data?.environment || 'SANDBOX',
        }));
      })
      .catch(() => {});
    return undefined;
  }, [companyId]);

  useEffect(() => {
    if (!showConfirmDialog) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowConfirmDialog(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showConfirmDialog]);

  const isConnected = status.connected;
  const statusLabel = isConnected
    ? `Connected · ${status.environment === 'SANDBOX' ? 'Sandbox' : 'Production'}`
    : 'Not connected';

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!companyId) return;
    setLoading(true);
    setAction('saving');
    setError('');
    setSuccess('');
    try {
      const payload = buildDeluxeSavePayload(form);
      await callApi(`/api/companies/${encodeURIComponent(companyId)}/payments/deluxe`, {
        companyId,
        clientId: payload.clientId,
        clientSecret: payload.clientSecret,
        accessToken: payload.accessToken,
        signatureKey: payload.signatureKey,
        partnerToken: payload.partnerToken,
        hpfToken: payload.hpfToken,
        environment: form.environment,
      });
      setSuccess('Deluxe settings saved.');
      setStatus({
        connected: true,
        environment: form.environment,
      });
    } catch (err) {
      setError(err?.message || 'Failed to save Deluxe settings');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!companyId) return;
    setLoading(true);
    setAction('testing');
    setError('');
    setSuccess('');
    try {
      const data = await callApi(`/api/companies/${encodeURIComponent(companyId)}/payments/deluxe/test`, {
        companyId,
        environment: form.environment,
      });
      const tested = data?.data?.environment || form.environment;
      setStatus((prev) => ({ ...prev, environment: tested }));
      setSuccess(
        `${tested === 'SANDBOX' ? 'Sandbox' : 'Production'} Deluxe connection is valid.`
      );
    } catch (err) {
      setError(err?.message || 'Deluxe test failed');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  const handleDisconnectConfirm = async () => {
    if (!companyId) return;
    setLoading(true);
    setAction('disconnecting');
    setError('');
    setSuccess('');
    try {
      await callApi(`/api/companies/${encodeURIComponent(companyId)}/payments/deluxe/disconnect`, {
        companyId,
      });
      setStatus({ connected: false, environment: 'SANDBOX' });
      setForm(EMPTY_FORM);
      setShowConfirmDialog(false);
      setSuccess('Deluxe disconnected.');
    } catch (err) {
      setError(err?.message || 'Failed to disconnect Deluxe');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  return (
    <div className="authorize-connect">
      <article className="an-card" aria-labelledby="deluxe-connect-title">
        <header className="an-card__header">
          <div className="an-card__brand">
            <div>
              <h2 id="deluxe-connect-title" className="an-card__title">
                Deluxe
                <button type="button" className="an-help" title={HELP_TEXT} aria-label={HELP_TEXT}>
                  ?
                </button>
              </h2>
              <p className="an-card__subtitle">Hosted Payment Form credentials for booking payments</p>
            </div>
          </div>
          <span className={`an-status an-status--${isConnected ? 'connected' : 'disconnected'}`} role="status">
            <span className="an-status__dot" aria-hidden="true" />
            {statusLabel}
          </span>
        </header>

        <div className="an-card__body">
          {isConnected ? (
            <div className="an-connected-note" role="status">
              <div>
                <strong>Ready to accept payments</strong>
                Your Deluxe credentials are saved. Use Test to verify they still work, or update the fields below and Save.
              </div>
            </div>
          ) : (
            <>
              <p className="an-description">
                Booking uses Deluxe Hosted Payment Form. Paste Client ID, Client Secret, and Access Token from your Deluxe credentials email.
              </p>
              <ul className="an-features">
                <li>Online booking and deposit payments</li>
                <li>Test credentials before accepting live payments</li>
                <li>Hosted Payment Form keeps card data off Neat Schedule</li>
              </ul>
            </>
          )}

          {error && <div className="an-alert an-alert--error" role="alert">{error}</div>}
          {success && <div className="an-alert an-alert--success" role="status">{success}</div>}

          <form className="an-form-grid" onSubmit={handleSave}>
            <div className="an-field">
              <label htmlFor="deluxeClientId" className="an-label">Client ID (required)</label>
              <input
                id="deluxeClientId"
                className="an-input"
                type="text"
                value={form.clientId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div className="an-field">
              <label htmlFor="deluxeClientSecret" className="an-label">Client Secret (required)</label>
              <input
                id="deluxeClientSecret"
                className="an-input"
                type="password"
                value={form.clientSecret}
                onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
                autoComplete="new-password"
              />
            </div>
            <div className="an-field">
              <label htmlFor="deluxeAccessToken" className="an-label">Access Token (Hosted Form)</label>
              <input
                id="deluxeAccessToken"
                className="an-input"
                type="password"
                value={form.accessToken}
                onChange={(e) => setForm({ ...form, accessToken: e.target.value })}
                autoComplete="new-password"
              />
            </div>
            <div className="an-field">
              <label htmlFor="deluxeSignatureKey" className="an-label">Signature Key (Apple Pay / Google Pay)</label>
              <input
                id="deluxeSignatureKey"
                className="an-input"
                type="password"
                value={form.signatureKey}
                onChange={(e) => setForm({ ...form, signatureKey: e.target.value })}
                autoComplete="new-password"
              />
            </div>
            <div className="an-field">
              <label htmlFor="deluxeEnvironment" className="an-label">Environment</label>
              <select
                id="deluxeEnvironment"
                className="an-select"
                value={form.environment}
                onChange={(e) => setForm({ ...form, environment: e.target.value })}
              >
                <option value="SANDBOX">Sandbox</option>
                <option value="PRODUCTION">Production</option>
              </select>
            </div>
          </form>

          <div className="an-actions">
            <button type="button" className="an-btn an-btn--primary" onClick={handleSave} disabled={loading}>
              {action === 'saving' ? 'Saving…' : 'Save credentials'}
            </button>
            <button type="button" className="an-btn an-btn--secondary" onClick={handleTest} disabled={loading || !isConnected}>
              {action === 'testing' ? 'Testing…' : 'Test connection'}
            </button>
            {isConnected && (
              <button type="button" className="an-btn an-btn--danger" onClick={() => setShowConfirmDialog(true)} disabled={loading}>
                Disconnect
              </button>
            )}
          </div>
          <p className="an-help-text">
            Choose Sandbox while using Deluxe test credentials. Production keys will fail against the sandbox API, and sandbox keys will fail against production. Signature Key is required if you want Apple Pay and Google Pay on booking; those wallet charges still go to this company&apos;s Deluxe account.
          </p>
        </div>
      </article>

      {showConfirmDialog && (
        <div className="an-modal-root" role="presentation">
          <button type="button" className="an-modal-backdrop" aria-label="Close dialog" onClick={() => setShowConfirmDialog(false)} />
          <div className="an-modal-panel" role="dialog" aria-modal="true" aria-labelledby="deluxe-disconnect-title">
            <h2 id="deluxe-disconnect-title" className="an-modal-title">Disconnect Deluxe?</h2>
            <p className="an-modal-body">
              Saved credentials will be removed from Neat Schedule. Your Deluxe account is not deleted — you can enter credentials again anytime.
            </p>
            <div className="an-modal-footer">
              <button type="button" className="an-btn an-btn--ghost" onClick={() => setShowConfirmDialog(false)}>Cancel</button>
              <button type="button" className="an-btn an-btn--danger" onClick={handleDisconnectConfirm} disabled={loading}>
                {action === 'disconnecting' ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeluxeConnectCard;
